import { useSizeClass } from "@/src/common/Utils/useSizeClass";
import { SizeClass } from "@/src/common/Utils/SizeClass";
import {
  AHEmptyScreen,
  AHSeparator,
  Box,
  useSpaceSize,
  useTodoSorter,
} from "arkh";
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
} from "react";
import { ActivityIndicator, ListRenderItem } from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { graphql, useLazyLoadQuery, usePaginationFragment } from "react-relay";

import { keyExtractorId } from "@/src/common/keyExtractors";
import { UserLoggedInNavigatorScreenProps } from "@/src/TopLevelNavigators/UserLoggedIn/UserLoggedInNavigatorParamList";
import { withWebModalStack } from "@/src/TopLevelNavigators/UserLoggedIn/withWebModalStack";
import { TodoCategoryAssignedToEmployeeQuery } from "./__generated__/TodoCategoryAssignedToEmployeeQuery.graphql";
import { usePersistedEmployeeId } from "@/src/OS/Login/usePersistedEmployeeId";
import { TodoCategoryAssignedToEmployeeList_TodoCategory$key } from "./__generated__/TodoCategoryAssignedToEmployeeList_TodoCategory.graphql";
import { useNavigationHeaderOptions } from "@/src/common/navigationHeaderOptions";
import { LazyQueryNetworkBoundrary } from "@/src/components/mobile/QueryNetworkBoundrary";

import { useNavigation } from "@react-navigation/native";
import Stretch from "../TodoCategory/stretch.json";
import { Lottie, LottieRef } from "../../components/Lottie";
import { FormattedMessage } from "react-intl";
import Animated from "react-native-reanimated";
import { defaultLayoutTransition } from "@/src/defaultLayoutTransition";
import { TodoListItem } from "@/src/components/TodoListItem";

export function getTodoFilterVariables(
  filterMode: "ASSIGNED" | "CREATED",
  employeeId: string | null
) {
  if (filterMode === "CREATED") {
    return {
      assignedToMe: undefined,
      assignedToEmployeeId: undefined,
      createdByEmployeeId: employeeId,
      createdByMe: true,
    };
  }

  // filterMode === "ASSIGNED"
  return {
    assignedToMe: true,
    assignedToEmployeeId: employeeId,
    createdByEmployeeId: undefined,
    createdByMe: undefined,
  };
}

function EmptyComponent(props: {
  categoryTitle: string;
  filterMode: "ASSIGNED" | "CREATED";
}) {
  const ref = useRef<LottieRef>(null);

  const nav = useNavigation();
  useEffect(() => {
    // hack to get lottie to play
    const listener = nav.addListener("transitionEnd" as any, () => {
      ref.current?.play();
    });
    ref.current?.play();
    return () => {
      listener();
    };
  }, [nav]);

  return (
    <Box style={{ flex: 1 }} color="surface">
      <AHEmptyScreen
        illustration={<Lottie source={Stretch} autoPlay loop />}
        title={
          props.filterMode === "CREATED" ? (
            <FormattedMessage
              description="TodoCategoryAssignedToEmployee Empty Screen Title Created"
              defaultMessage="No {categoryTitle} created by you"
              values={{ categoryTitle: props.categoryTitle.toLowerCase() }}
            />
          ) : (
            <FormattedMessage
              description="TodoCategoryAssignedToEmployee Empty Screen Title Assigned"
              defaultMessage="No {categoryTitle} assigned to you"
              values={{ categoryTitle: props.categoryTitle.toLowerCase() }}
            />
          )
        }
        subtitle={
          props.filterMode === "CREATED" ? (
            <FormattedMessage
              description="TodoCategoryAssignedToEmployee Empty Screen SubTitle Created"
              defaultMessage="All {categoryTitle} created by you are displayed here."
              values={{
                categoryTitle: props.categoryTitle.toLowerCase(),
              }}
            />
          ) : (
            <FormattedMessage
              description="TodoCategoryAssignedToEmployee Empty Screen SubTitle Assigned"
              defaultMessage="All {categoryTitle} assigned to you are displayed here."
              values={{
                categoryTitle: props.categoryTitle.toLowerCase(),
              }}
            />
          )
        }
      />
    </Box>
  );
}

function TodoCategoryAssignedToEmployeeList(props: {
  category: TodoCategoryAssignedToEmployeeList_TodoCategory$key;
  openTodo: (fromResponse: boolean, todoId: string) => void;
  filterMode: "ASSIGNED" | "CREATED";
}) {
  const { data, hasNext, isLoadingNext, loadNext } = usePaginationFragment(
    graphql`
      fragment TodoCategoryAssignedToEmployeeList_TodoCategory on TodoCategory
      @refetchable(
        queryName: "TodoCategoryAssignedToEmployeeListPaginationQuery"
      )
      @argumentDefinitions(
        first: { type: "Int" }
        after: { type: "String" }
        assignedToMe: { type: "Boolean" }
        assignedToEmployeeId: { type: "ID" }
        createdByEmployeeId: { type: "ID" }
        createdByMe: { type: "Boolean" }
      ) {
        id
        title
        todos(
          first: $first
          after: $after
          assignedToMe: $assignedToMe
          assignedToEmployeeId: $assignedToEmployeeId
          createdByEmployeeId: $createdByEmployeeId
          createdByMe: $createdByMe
          includeSubTodos: true
        ) @connection(key: "TodoCategoryAssignedToEmployeeList_todos") {
          __id
          edges {
            node {
              id
              createdAtDate {
                isoString
              }
              ordinal
              ...TodoListItem_todo
            }
          }
        }
      }
    `,
    props.category
  );
  const insets = useSafeAreaInsets();
  const isCompact = useSizeClass().width === SizeClass.COMPACT;
  const medium = useSpaceSize("medium");
  const containerStyle = useMemo(
    () => ({
      paddingHorizontal: medium,
      paddingTop: medium,
      paddingBottom: medium + (isCompact ? insets.bottom : 0),
    }),
    [insets.bottom, isCompact, medium]
  );

  const todoSorter = useTodoSorter();
  const edges = useMemo(
    () =>
      data.todos.edges
        .flatMap((edge) => (edge.node ? [edge.node] : []))
        .sort(todoSorter),
    [data.todos.edges, todoSorter]
  );

  const renderItem: ListRenderItem<(typeof edges)[number]> = useCallback(
    ({ item, index }) => (
      <TodoListItem
        todo={item}
        isFirst={index === 0}
        isLast={index === edges.length - 1}
        openTodo={props.openTodo}
      />
    ),
    [edges.length, props.openTodo]
  );

  return (
    <>
      {data.todos.edges.length === 0 ? (
        <EmptyComponent
          categoryTitle={data.title}
          filterMode={props.filterMode}
        />
      ) : (
        <Animated.FlatList
          contentContainerStyle={containerStyle}
          data={edges}
          keyExtractor={keyExtractorId}
          renderItem={renderItem}
          ItemSeparatorComponent={AHSeparator}
          onEndReachedThreshold={0.6}
          onEndReached={() => {
            if (hasNext && !isLoadingNext) {
              loadNext(20);
            }
          }}
          ListFooterComponent={
            isLoadingNext ? (
              <Box spaceTop="medium">
                <ActivityIndicator size="large" />
              </Box>
            ) : null
          }
          itemLayoutAnimation={defaultLayoutTransition}
        />
      )}
    </>
  );
}

function InnerTodoCategoryAssignedToEmployee(
  props: UserLoggedInNavigatorScreenProps<"TodoCategoryAssignedToEmployee"> & {
    fetchKey: string;
  }
) {
  const getOptions = useNavigationHeaderOptions();

  const { currentEmployeeId } = usePersistedEmployeeId();
  const filterMode = props.route.params.filterMode ?? "ASSIGNED";

  const data = useLazyLoadQuery<TodoCategoryAssignedToEmployeeQuery>(
    graphql`
      query TodoCategoryAssignedToEmployeeQuery(
        $todoCategoryId: ID!
        $first: Int
        $after: String
        $assignedToMe: Boolean
        $assignedToEmployeeId: ID
        $createdByEmployeeId: ID
        $createdByMe: Boolean
      ) {
        todoCategory(id: $todoCategoryId) {
          title
          ...TodoCategoryAssignedToEmployeeList_TodoCategory
            @arguments(
              first: $first
              after: $after
              assignedToMe: $assignedToMe
              assignedToEmployeeId: $assignedToEmployeeId
              createdByEmployeeId: $createdByEmployeeId
              createdByMe: $createdByMe
            )
        }
      }
    `,
    {
      todoCategoryId: props.route.params.todoCategoryId,
      first: 20,
      ...getTodoFilterVariables(filterMode, currentEmployeeId ?? null),
    },
    {
      fetchKey: props.fetchKey,
      fetchPolicy: "store-and-network",
    }
  );

  useLayoutEffect(() => {
    props.navigation.setOptions(
      getOptions({
        type: "CLOSE",
        onClose: () => {
          props.navigation.goBack();
        },
        title: data.todoCategory.title,
      })
    );
  }, [getOptions, props.navigation, data.todoCategory.title]);

  const openTodo = (fromResponse: boolean, todoId: string) => {
    props.navigation.navigateDeprecated("TodoEdit", {
      todoId,
    });
  };

  return (
    <TodoCategoryAssignedToEmployeeList
      category={data.todoCategory}
      openTodo={openTodo}
      filterMode={filterMode}
    />
  );
}

export const TodoCategoryAssignedToEmployee = withWebModalStack(
  (
    props: UserLoggedInNavigatorScreenProps<"TodoCategoryAssignedToEmployee">
  ) => {
    const getOptions = useNavigationHeaderOptions();
    useLayoutEffect(() => {
      props.navigation.setOptions(
        getOptions({
          type: "CLOSE",
          onClose: () => {
            props.navigation.goBack();
          },
          title: "",
        })
      );
    }, [getOptions, props.navigation]);
    return (
      <LazyQueryNetworkBoundrary newFetchKeyOnNavigation>
        {(fetchKey) => (
          <InnerTodoCategoryAssignedToEmployee {...props} fetchKey={fetchKey} />
        )}
      </LazyQueryNetworkBoundrary>
    );
  }
);
